<template>
  <v-card>
    <confirmation-dialog ref="confirm" />
    <v-card-text>
      <v-row class="align-center justify-right">
        <v-col
          cols="6"
          md="3"
        >
          <search-field v-model="search" />
        </v-col>
        <v-spacer />
        <v-col
          cols="3"
          class="text-right"
        >
          <add-new-button to="/document/new" />
        </v-col>
      </v-row>

      <!-- TODO search items/direction should be prefs. Saved to localStorage? -->
      <v-data-table
        :headers="headers"
        :items="docsMapped"
        :loading="loading"
        :search="search"
        :loading-text="loadingOrNoDataText"
        :sort-by="'modified'"
        :sort-desc="true"
        :class="{ 'hide-colgroup table-fixed': $vuetify.breakpoint.xs }"
      >
        <div>
          <v-icon small>
            mdi-account-multiple-outline
          </v-icon>
        </div>

        <div>Stage &amp; Status</div>

        <div>Actions</div>

        <div>
          <v-icon small>
            mdi-clock-alert-outline
          </v-icon>
        </div>
        <template #[`item.counterParties`]="{ item }">
          {{ counterPartyNames(item) }}
        </template>

        <template #[`no-data`]>
          <div class="darkslategray--text font-weight-bold py-4">
            <v-row justify="center">
              Hi! You don't currently have any documents on your dashboard. You
              can learn more about how to use your dashboard by clicking the
              "Help" button in the top-right.
            </v-row>
            <v-row justify="space-around">
              <v-spacer />
              <v-col>
                <video
                  title="Welcome to your dashboard"
                  controls="controls"
                  autoplay
                  playsinline
                  width="600"
                  height="309"
                >
                  <source
                    src="/video/dashboard.webm"
                    type="video/webm"
                  >
                  <source
                    src="/video/dashboard.mp4"
                    type="video/mp4"
                  >
                </video>
                <p>Your browser does not support video.</p>
              </v-col>
              <v-spacer />
            </v-row>
          </div>
        </template>
        <template #[`expanded-item`]="{ item }">
          <tr class="d-flex justify-end flex-wrap">
            <td class="d-flex align-center px-2">
              <doc-job-id
                :job-id="item.jobId"
                :job-name="item.jobName"
              />
            </td>
            <td class="d-flex align-center px-2">
              <stages-status-indicator
                :progress-items="item.progressItems"
                :progress-item="item.progressItem"
                :status="getDocumentStatusDisplay(item)"
              />
            </td>
            <!-- <td class="d-flex align-center px-2">
              <doc-progress :progress="item.progress" />
            </td> -->
            <td class="d-flex align-center px-2">
              <span class="text-no-wrap">
                <v-btn
                  text
                  icon
                  color="primary"
                  title="Edit"
                  small
                  :to="item.to"
                >
                  <v-icon small> mdi-pencil </v-icon>
                </v-btn>
                <!-- TODO ALPHA: check this works for non-admins. -->
              </span>
            </td>
          </tr>
        </template>

        <template #[`item.jobId`]="{ item }">
          <doc-job-id
            :job-id="item.jobId"
            :job-name="item.jobName"
          />
        </template>

        <template #[`item.documentType`]="{ item }">
          <document-type-label :document-type="item.documentType" />
        </template>

        <template #[`item.name`]="{ item }">
          <div class="">
            <a
              :key="item.id"
              class="text-decoration-none"
              :title="item.name"
              :class="{
                'line-clamp-1': $vuetify.breakpoint.smOnly,
                'line-clamp-2': $vuetify.breakpoint.mdAndUp
              }"
              @click.prevent="documentClick(item)"
            >
              {{ item.name }}
            </a>
            <span class="d-none d-sm-inline d-md-none opac-65">(<doc-job-id
              :job-id="item.jobId"
              :job-name="item.jobName"
            />)</span>
          </div>
        </template>

        <template #[`item.assignedToSort`]="{ item }">
          <people
            :assigned-to="item.assignedTo"
            :assigned-to-client="item.assignedToClient"
            :assigned-to-me="item.assignedToMe"
            :assigned-to-role="item.assignedToRole"
            :non-client-roles-count="item.nonClientRolesCount"
            :roles="item.roles"
          />
        </template>

        <!-- Stage -->
        <template #[`item.stageSort`]="{ item }">
          <div class="stage-container">
            <stages-status-indicator
              :progress-items="item.progressItems"
              :progress-item="item.progressItem"
              :status="getDocumentStatusDisplay(item)"
            />
            <div class="progress-container">
              <v-progress-circular
                v-show="item.sending"
                indeterminate
                color="primary"
                size="20"
              />
            </div>
          </div>
        </template>

        <!-- Progress -->
        <!-- <template v-slot:item.progress="{ item }">
          <doc-progress :progress="item.progress" />
        </template> -->

        <template #[`item.actions`]="{ item }">
          <span class="text-no-wrap">
            <table-actions
              :actions="getActionsTable(item)"
              @action="onActionEvent"
            />
          </span>
        </template>

        <template #[`item.modified`]="{ item }">
          <modified-column :last-action-info="item.lastActionInfo" />
        </template>
      </v-data-table>
    </v-card-text>

    <!-- TODO MVP Paging
      <v-btn small text>Load More</v-btn>
      <v-btn small text class="hidden-xs-only">Load All</v-btn>-->

    <!-- TODO MVP doc sharing (with people other than roles, by client) -->
    <send-action-dialog
      v-if="isSendActionDialog"
      :document="selectedDocument"
      :show-dialog="isSendActionDialog"
      @close="
        sendDialog = false
        selectedDocument = null
      "
    />

    <v-dialog
      v-model="previewDialog"
      persistent
      min-height="700px"
    >
      <v-card>
        <v-card-title>
          Preview
          <v-spacer />
          <v-btn
            color="primary"
            small
            text
            @click.stop="handlePreviewClose"
          >
            Close
          </v-btn>
        </v-card-title>
        <div class="preview-body-ctn">
          <iframe
            v-show="iframeLoaded"
            ref="documentFrame"
            class="preview-iframe"
            title="Document Preview"
            @load="onIframeLoad"
          />
          <div
            v-show="!iframeLoaded"
            class="preview-spinner"
          >
            <v-progress-circular
              :indeterminate="true"
              color="primary"
            />
          </div>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="signingDialog"
      persistent
      min-height="700px"
    >
      <v-card>
        <v-card-title>
          Sign
          <v-spacer />
          <v-btn
            color="primary"
            small
            text
            @click.stop="handleSigningDialogClose"
          >
            Close
          </v-btn>
        </v-card-title>
        <div class="preview-body-ctn">
          <iframe
            class="annature-iframe"
            title="Sign"
            :src="signingEndpoint"
          />
        </div>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="showUploadNewVersionDialog"
      max-width="500px"
      scrollable
      @keydown.esc="showUploadNewVersionDialog = false"
    >
      <dashboard-upload-new-version
        :document="documentToReplace"
        @close="showUploadNewVersionDialog = false"
      />
    </v-dialog>
  </v-card>
</template>

<script>
import { fsdb, auth, firebase } from '../firebase'
import { allowSigning, allowUpload, getDocumentStatusDisplay } from '@/utils/dashboard'
import ConfirmationDialog from '@/components/ConfirmationDialog.vue'
import StagesStatusIndicator from '@/components/StagesStatusIndicator.vue'
import People from '@/components/People.vue'
import ModifiedColumn from '@/components/ModifiedColumn.vue'
import DocJobId from '@/components/DocJobId.vue'
import SearchField from '@/components/SearchField'
import AddNewButton from '@/components/AddNewButton'
import DownloadDocumentFile from '@/mixins/DownloadDocumentFile'
import {
  isSingleUserAccount,
  isStandardBusinessAccount
} from '@/dataAccess/accountTypes'
import TableActions from './TableActions.vue'
import { getCounterPartyNames } from '@/dataAccess/parties'
import toMaterialStyle from '../utils/toMaterialStyle'
import DocumentTypeLabel from './DocumentTypeLabel.vue'
import DashboardUploadNewVersion from '@/components/dashboard/DashboardUploadNewVersion'

const SendActionDialog = () => import('@/components/SendActionDialog')

export default {
  name: 'DocListCard',
  components: {
    ConfirmationDialog,
    DashboardUploadNewVersion,
    DocJobId,
    ModifiedColumn,
    People,
    StagesStatusIndicator,
    SendActionDialog,
    SearchField,
    AddNewButton,
    DocumentTypeLabel,
    TableActions
  },
  mixins: [DownloadDocumentFile],
  props: {
    cardTitle: { type: String, default: 'Document List' },
    docList: { type: Array, default: () => [] },
    loading: { type: Boolean, default: true },
    numDocs: { type: Number, default: 0 },
    isIndividual: { type: Boolean, default: true }
  },
  data: () => ({
    getDocumentStatusDisplay,
    documentToReplace: null,
    search: '',
    showColleaguesDocs: true,
    sendDialog: false,
    shareDialog: false,
    previewDialog: false,
    iframeLoaded: false,
    selectedDocument: null,
    showUploadNewVersionDialog: false,
    signingEndpoint: '',
    signingDialog: false,
    topics: []
  }),
  computed: {
    isSendActionDialog() {
      return !!(this.sendDialog && this.selectedDocument)
    },
    selectedAccountIsOpenLaw() {
      return (
        this.isAdmin && this.account && this.account.shortName === 'OpenLaw'
      )
    },
    isSingleUser() {
      return this.account && isSingleUserAccount(this.account.type)
    },
    isStandardBusiness() {
      return this.account && isStandardBusinessAccount(this.account.type)
    },
    headers() {
      const headers = [
        {
          text: 'Job Id',
          value: 'jobId',
          sortable: true,
          align: ' px-sm-only-3 pr-0 text-no-wrap',
          hidden: this.isIndividual || this.$vuetify.breakpoint.sm,
          width: '5%'
        },
        {
          text: 'Document',
          value: 'name',
          sortable: true,
          align: ' px-sm-only-3 '
        },
        {
          text: 'Type',
          value: 'documentType',
          filterable: false,
          sortable: true,
          align: ' px-sm-only-3 '
        },
        {
          text: 'For',
          value: 'counterParties',
          filterable: false,
          sortable: true,
          hidden: this.isIndividual,
          align: ' px-sm-only-3 '
        },
        {
          text: 'From',
          value: 'firstParty.name',
          filterable: false,
          sortable: true,
          align: ' px-sm-only-3 '
        },
        // { text: 'People', value: 'assignedToSort', sortable: true, align: 'sm-center text-md-left px-sm-2' },
        {
          text: 'Stage',
          value: 'stageSort',
          filterable: false,
          sortable: true,
          align: 'center px-sm-2'
        },
        // { text: 'Progress', value: 'progress', align: 'center hidden-sm-and-down px-sm-2', sortable: true },

        // hidden but searchable due to align: ' d-none' -- the leading space is important
        // not last because we want our last-child CSS to work!
        { text: 'DocId', filterable: false, value: '.key', align: ' d-none' },
        {
          text: 'RoleIds',
          filterable: false,
          value: 'roleUsers',
          align: ' d-none'
        },
        {
          text: 'SearchStr',
          filterable: false,
          value: 'searchStr',
          align: ' d-none'
        },

        {
          text: 'Actions',
          value: 'actions',
          filterable: false,
          sortable: false,
          align: 'center px-sm-2'
        },
        {
          text: 'Modified',
          value: 'modified',
          filterable: false,
          sortable: true,
          align: 'center px-sm-2',
          hidden: this.isIndividual
        }
      ]

      return headers.filter((header) => !header.hidden)
    },
    loadingOrNoDataText() {
      return 'Loading your dashboard'
    },
    docsMapped() {
      if (this.isSingleUser || this.isStandardBusiness) {
        return this.docList.map((item) => ({
          ...item,
          searchStr: JSON.stringify(item).toLowerCase()
        }))
      }
      return (
        this.docList
          .filter((item) => item.ownDoc || this.showColleaguesDocs)
          /* TODO this should be in getters? */
          .map((item) => ({
            ...item,
            searchStr: JSON.stringify(item).toLowerCase()
          }))
      )
    },
    docsByIdJsoned() {
      return this.docsMapped.reduce(
        (acc, item) => ({
          ...acc,
          [item['.key']]: JSON.stringify(item).toLowerCase()
        }),
        {}
      )
    },
    adminViewingOther() {
      return this.isAdmin && this.account.id !== this.claims.accountId
    }
  },
  watch: {
    docList: function (list) {
      if (this.previewDialog && this.selectedDocument) {
        const idx = list.findIndex((d) => d.id === this.selectedDocument.id)
        if (idx !== -1) {
          this.topics = list[idx].topics
        }
      }
    }
  },
  beforeCreate() {
    this.dispatchActions(['get_accountStatus'])
    this.mapGetters(['usersById', 'isAdmin', 'featureEnabled'])
    this.mapStates(['account', 'claims', 'appUser', 'user', 'accountStatus', 'mfaEnabled'])
    this.toMaterialStyle = toMaterialStyle
  },
  async created() {
    const { toSign: documentId } = this.$route.query
    if (documentId) {
      await this.signForDocumentId(documentId)
    }
  },
  methods: {
    isSigningStage(item) {
      return (
        item.stage.toLowerCase() === 'counterparty' ||
        item.stage.toLowerCase() === 'signature'
      )
    },
    selectDocumentItem(item, dialog) {
      this.selectedDocument = item
      this[dialog] = true
    },
    async deleteDocument(document) {
      const name = document.name
      if (
        await this.$refs.confirm.open(
          `Delete ${name}?`,
          'Are you sure you want to delete this document?'
        )
      ) {
        this.$handlingError(`Error deleting document "${name}"`, async () => {
          await this.$api.deleteDocument(document)
          this.$showSuccessbar(`Deleted document "${name}"`)
        })
      }
    },
    allowSigning(item) {
      const { emailVerified, email, uid } = this.user
      const mfaEnabled = this.featureEnabled('mfa') ? this.mfaEnabled : true
      return allowSigning(item, { uid, email, emailVerified, mfaEnabled })
    },
    allowSending(item) {
      if (!item.isDocument) {
        return { value: false, reason: 'You must finish creating this document before you can send it' }
      }
      if (item.sentForSignature) {
        return { value: false, reason: 'This document has already been sent' }
      }
      if (this.insufficientCredits(item)) {
        return { value: false, reason: 'You do not have enough credits to send this document' }
      }
      return { value: true, reason: '' }
    },
    allowDownload(item) {
      if (!item.isDocument) {
        return { value: false, reason: 'You need to finish creating this document before you can download it' }
      }
      if (this.insufficientCredits(item)) {
        return { value: false, reason: 'You do not have enough credits to download this document' }
      }
      return { value: true, reason: '' }
    },
    allowUpload(item) {
      return allowUpload(item, { uid: this.user.uid })
    },
    insufficientCredits({ paymentStatus: { paid, creditCost } }) {
      if (!this.featureEnabled('accountStatus')) {
        return false
      }
      if (paid) {
        return false
      }
      return this.accountStatus.credits < creditCost
    },
    canDelete(item) {
      if (!item.sentForSignature) {
        return true
      }
      if (item.signStatus === 'Annature_Waiting_Recipient_Sign') {
        return true
      }
      return false
    },
    async documentClick(item) {
      if (this.canEdit(item)) {
        this.editDocument(item)
        return
      }
      this.previewDocument(item)
    },
    canEdit(item) {
      return !item.isDocument
    },
    editDocument(item) {
      this.$router.push({
        name: 'documents',
        params: { documentId: item.id, new: 'new' }
      })
    },
    async previewDocument(item) {
      try {
        this.topics = item.topics
        this.selectedDocument = item
        const documentId = item.id

        // GOOGLE DOCS VIEWER
        this.iframeLoaded = false
        this.previewDialog = true
        if (this.$refs.documentFrame) {
          this.$refs.documentFrame.src = ''
        }
        const response = await this.$firePost('documentSignedUrl', auth, {
          documentId
        })
        this.$refs.documentFrame.src = `https://docs.google.com/gview?url=${response.data.signedUrl}&embedded=true`
      } catch (e) {
        this.iframeLoaded = true
        this.$showErrorbar('Error fetching document. Try again later.')
        this.$Sentry.captureException(e)
      }
    },
    onIframeLoad() {
      if (this.$refs.documentFrame && this.$refs.documentFrame.src) {
        this.iframeLoaded = true
      }
    },
    handlePreviewClose() {
      this.iframeLoaded = false
      this.previewDialog = false
    },
    handleSigningDialogClose() {
      this.signingDialog = false
      this.signingEndpoint = ''
    },
    async getSigningEndpoint(item) {
      this.signForDocumentId(item.id)
    },
    async signForDocumentId(documentId) {
      this.$handlingError('Error fetching signing endpoint', async () => {
        const response = await this.$api.getSignatureEndpoint({
          id: documentId
        })
        this.signingEndpoint = response.data.endpoint
        this.signingDialog = true
      })
    },
    async addTopic(message) {
      try {
        const docRef = fsdb
          .collection('documents')
          .doc(this.selectedDocument.id)
        const topic = {
          message,
          username: this.appUser.name,
          userId: this.appUser.id,
          createdAt: new Date(),
          comments: [],
          resolved: false,
          resolvedBy: ''
        }
        await docRef.update({
          topics: firebase.firestore.FieldValue.arrayUnion(topic)
        })
      } catch (ex) {
        this.$showErrorbar(ex.message || ex)
      }
    },
    async addComment({ comment, topicIndex }) {
      try {
        const docRef = fsdb
          .collection('documents')
          .doc(this.selectedDocument.id)
        await fsdb.runTransaction(async (transaction) => {
          const doc = await transaction.get(docRef)
          if (!doc.exists) {
            throw new Error('Document does not exist')
          }
          const docData = doc.data()
          docData.topics[topicIndex].comments.push({
            message: comment,
            username: this.appUser.name,
            userId: this.appUser.id,
            createdAt: new Date()
          })
          transaction.update(docRef, docData)
        })
      } catch (ex) {
        this.$showErrorbar(ex.message || ex)
      }
    },
    async resolveTopic({ topicIndex }) {
      try {
        const docRef = fsdb
          .collection('documents')
          .doc(this.selectedDocument.id)
        await fsdb.runTransaction(async (transaction) => {
          const doc = await transaction.get(docRef)
          if (!doc.exists) {
            throw new Error('Document does not exist')
          }
          const docData = doc.data()
          docData.topics[topicIndex].resolved = true
          docData.topics[topicIndex].resolvedBy = this.appUser.name
          transaction.update(docRef, docData)
        })

        const docData = await docRef.get()
        const allResolved = docData.data().topics.every((t) => t.resolved)

        if (allResolved) {
          // Send email to doc owner with comments
          await this.$firePost('document/notify-comments', auth, {
            documentId: this.selectedDocument.id
          })
        }
      } catch (ex) {
        this.$showErrorbar(ex.message || ex)
      }
    },
    uploadNewVersion(item) {
      this.documentToReplace = item
      this.showUploadNewVersionDialog = true
    },
    onActionEvent({ actionEvent, itemValue, type }) {
      try {
        this[actionEvent](itemValue, type)
      } catch (error) {
        this.$showErrorbar(
          `Unable to call the event ${actionEvent}, please check that the action event match with your request.`
        )
      }
    },
    getActionsTable(item) {
      const individualOrBusiness = this.isSingleUser || this.isStandardBusiness
      const isOriginatingAccount = this.appUser.accountId === item.accountId
      return [
        {
          hoverColor: 'primary',
          className: item.sentForSignature ? 'icon-hide' : '',
          isDisable: !this.allowSending(item).value,
          disabledReason: this.allowSending(item).reason,
          event: 'selectDocumentItem',
          title: 'Send',
          icon: 'send',
          type: 'sendDialog',
          item
        },
        {
          hoverColor: 'primary',
          className: !individualOrBusiness ? 'icon-hide' : '',
          isDisable: !this.allowDownload(item).value,
          disabledReason: this.allowDownload(item).reason,
          event: 'downloadDocumentFile',
          title: 'Download',
          icon: 'download',
          type: '',
          item
        },
        {
          hoverColor: 'primary',
          className: !individualOrBusiness ? 'icon-hide' : '',
          isDisable: !this.allowUpload(item).value,
          disabledReason: this.allowUpload(item).reason,
          event: 'uploadNewVersion',
          title: 'Upload new version',
          icon: 'upload',
          type: '',
          item
        },
        {
          hoverColor: 'primary',
          className: !individualOrBusiness ? 'icon-hide' : '',
          isDisable: !this.allowSigning(item).value,
          event: 'getSigningEndpoint',
          disabledReason: this.allowSigning(item).reason,
          title: 'Sign',
          icon: 'gesture',
          type: '',
          item
        },
        {
          hoverColor: 'primary',
          className: isOriginatingAccount ? '' : 'icon-hide',
          isDisable: !this.canDelete(item),
          event: 'deleteDocument',
          title: 'Delete',
          icon: 'delete',
          type: '',
          item
        }
      ]
    },
    counterPartyNames: getCounterPartyNames
  }
}
</script>

<style lang="scss">
.icon-hide {
  opacity: 0;
  pointer-events: none;
  display: none;
}

.image-placeholder {
  opacity: 0.2;
}

.preview-body-ctn {
  width: 100%;
  height: 80vh;
  display: flex;

  .annature-iframe {
    height: 100%;
    width: 100%;
  }

  .preview-iframe {
    height: 100%;
    width: 100%;
  }

  .preview-spinner {
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .comments-section {
    width: 40%;
    padding: 0 10px;
  }

  .spinner {
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.stage-container {
  display: flex;
  flex-direction: row;
  .progress-container {
    width: 20px;
    height: 20px;
    align-self: center;
    padding-left: 0.5rem;
    margin-bottom: 10px;
  }
}
</style>
